import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import { IoChevronBackCircle, IoChevronForwardCircle, IoEyeOutline } from "react-icons/io5";
import { format, subDays, getDaysInMonth } from "date-fns";
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { useUser } from '../../context/UserContext';

function MonthlyAttendanceReport() {
  const [searchParams] = useSearchParams();
  const [report, setReport] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageSize] = useState(10);
  const [exportloading, setExportLoading] = useState(false);

  const [month, setMonth] = useState(() => new Date().getMonth() + 1);
  const [year, setYear] = useState(() => new Date().getFullYear());

  const today = new Date();
  const isCurrentMonthSelected =
    month === today.getMonth() + 1 && year === today.getFullYear();

  const yesterday = subDays(new Date(), 1);
  const searchTerm = searchParams.get("search") || "";

  // Popup modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");

  // Export modal state
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { currentUser } = useUser();

  // Pastel colors for each status
  const statusColors = {
    Present: "bg-green-200 text-green-800",
    Absent: "bg-red-200 text-red-800",
    HalfDay: "bg-orange-200 text-orange-800",
    CompOff: "bg-purple-200 text-purple-800",
    Late: "bg-yellow-200 text-yellow-800",
    EarlyOut: "bg-pink-200 text-pink-800",
    OnDuty: "bg-blue-200 text-blue-800",
    Holiday: "bg-teal-200 text-teal-800",
    CompOffApply: "bg-indigo-200 text-indigo-800",
    Pending: "bg-gray-200 text-gray-800",
    EarnedLeave: "bg-emerald-200 text-emerald-800",
    SickLeave: "bg-rose-200 text-rose-800",
    CompOffLeave: "bg-fuchsia-200 text-fuchsia-800",
  };

  // Days till yesterday
  const daysTillYesterday = (() => {
    const isSameMonth = yesterday.getMonth() + 1 === month && yesterday.getFullYear() === year;
    if (isSameMonth) {
      return yesterday.getDate();
    } else {
      return getDaysInMonth(new Date(year, month - 1));
    }
  })();

  const fetchReport = async (page = 1) => {
    setLoading(true);
    try {
      const response = await api.get(`/api/daily-attendance/monthly-reports`, {
        params: {
          month,
          year,
          page: page - 1,
          size: pageSize,
          search: searchTerm,
        },
      });

      const data = response.data;
      setReport(data.content || []);
      setTotalPages(data.totalPages || 0);
      setCurrentPage(data.number + 1 || 1);
    } catch (error) {
      console.error('Failed to fetch monthly attendance report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (userId, employeeName) => {
    try {
      const res = await api.get(`/api/daily-attendance/get-monthly-data`, {
        params: { year, month, userId }
      });
      setAttendanceData(res.data);
      setSelectedEmployee(employeeName);
      setIsModalOpen(true);
    } catch (err) {
      console.error("Failed to fetch user attendance details:", err);
    }
  };

  useEffect(() => {
    fetchReport(currentPage);
  }, [currentPage, month, year, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
    fetchReport(1);
  }, [searchParams]);

  const handleCurrentMonthDownload = async () => {
    if (!currentUser?.token) {
      toast.error("User not authenticated");
      return;
    }

    setExportLoading(true);

    try {
      const response = await api.get(`/attendance/download?year=${year}&month=${month}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Attendance_${month}_${year}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setIsExportModalOpen(false);
      toast.success("File downloaded successfully", { position: "bottom-right", autoClose: 3000 });
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Error downloading file", { position: "bottom-right", autoClose: 3000 });
    } finally {
      setExportLoading(false);
    }
  };

  const handleDateRangeDownload = async () => {
    if (!currentUser?.token) {
      toast.error("User not authenticated");
      return;
    }

    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates", { position: "bottom-right", autoClose: 3000 });
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.error("Start date must be before end date", { position: "bottom-right", autoClose: 3000 });
      return;
    }

    setExportLoading(true);

    try {
      const response = await api.get(`/attendance-range?startDate=${startDate}&endDate=${endDate}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Attendance_${startDate}_to_${endDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setIsExportModalOpen(false);
      toast.success("File downloaded successfully", { position: "bottom-right", autoClose: 3000 });
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Error downloading file", { position: "bottom-right", autoClose: 3000 });
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 w-full mx-auto space-y-6">
      <motion.div
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h2 className="text-2xl font-bold text-slate-800">Monthly Attendance Report</h2>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsExportModalOpen(true)}
              className="bg-green-600 rounded-full px-4 py-2 text-white flex items-center gap-2 hover:bg-green-700 transition-colors"
            >
              Export
            </button>
            <button
              className="rounded-full text-slate-700 hover:text-slate-900 focus:outline-none"
              onClick={() =>
                setMonth((prevMonth) => {
                  if (prevMonth === 1) {
                    setYear((prevYear) => prevYear - 1);
                    return 12;
                  }
                  return prevMonth - 1;
                })
              }
              aria-label="Previous Month"
            >
              <IoChevronBackCircle className="text-4xl" />
            </button>

            <span className="font-semibold text-slate-700 text-xl select-none">
              {format(new Date(year, month - 1), "MMMM yyyy")}
            </span>

            <button
              className={`rounded-full text-slate-700  focus:outline-none ${isCurrentMonthSelected ? 'opacity-40 cursor-not-allowed' : ''}`}
              onClick={() => {
                if (isCurrentMonthSelected) return;
                setMonth((prevMonth) => {
                  if (prevMonth === 12) {
                    setYear((prevYear) => prevYear + 1);
                    return 1;
                  }
                  return prevMonth + 1;
                });
              }}
              aria-label="Next Month"
              disabled={isCurrentMonthSelected}
            >
              <IoChevronForwardCircle className="text-4xl" />
            </button>
          </div>

          <div className="text-sm text-slate-500 mt-1 sm:mt-0">
            Till {format(yesterday, 'dd MMM yyyy')}
          </div>
        </div>
      </motion.div>

      <motion.div
        className="bg-white rounded-2xl shadow-md overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-slate-700">
            <thead className="bg-white border-b border-slate-200">
              <tr>
                {['Sl. No', 'Employee Name', 'Department', 'Present', 'Absent', 'On duty', 'Earned leave', 'Sick leave', 'Comp off leave', 'Comp off', 'Worked Days  ', 'Worked Hours', 'Actions'].map((heading, idx) => (
                  <th key={idx} className="px-6 py-4 text-left font-semibold">{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                      </svg>
                    </div>
                  </td>
                </tr>
              ) : report.length === 0 ? (
                <tr>
                  <td colSpan="12" className="text-center py-8 text-slate-500">No attendance records found.</td>
                </tr>
              ) : (
                report.map((record, index) => {
                  const workedDays = record.absentDays !== null ? Math.max(0, (daysTillYesterday - record.absentDays).toFixed(1)) : '-';

                  return (
                    <tr key={index} className="hover:bg-slate-100/50 transition-all duration-200">
                      <td className="px-6 py-4">{(currentPage - 1) * pageSize + index + 1}</td>
                      <td className="px-6 py-4 font-medium">{record.employeeName}</td>
                      <td className="px-6 py-4">{record.department}</td>

                      <td className="px-6 py-4">{record.presentDays ?? 0}</td>
                      <td className="px-6 py-4">{record.absentDays ?? 0}</td>

                      <td className="px-6 py-4">{record.onDutyDays ?? 0}</td>

                      <td className="px-6 py-4">{record.earnedLeave ?? 0}</td>
                      <td className="px-6 py-4">{record.sickLeave ?? 0}</td>
                      <td className="px-6 py-4">{record.compOffLeave ?? 0}</td>

                      <td className="px-6 py-4">{record.compOff ?? 0}</td>

                      <td className="px-6 py-4 font-semibold text-green-700">{workedDays}</td>
                      <td className="px-6 py-4 font-semibold text-blue-700">{record.totalWorkedHours ? record.totalWorkedHours.toFixed(2) : '0.00'}</td>
                      <td className="px-6 py-4">
                        <div className="flex justify-center">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleView(record.employeeId, record.employeeName)}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title="View Details"
                          >
                            <IoEyeOutline className="text-xl" />
                          </motion.button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {report.length > 0 && (
            <div className="px-6 py-4 border-t border-slate-100">
              <Stack spacing={2} className="flex justify-end">
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                  variant="outlined"
                  shape="full"
                  size="large"
                  showFirstButton
                  showLastButton
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: "#475569",
                      borderColor: "#e2e8f0",
                    },
                    "& .Mui-selected": {
                      backgroundColor: "#475569 !important",
                      color: "#fff",
                    },
                  }}
                />
              </Stack>
            </div>
          )}
        </div>
      </motion.div>

      {/* Export Modal */}
      {isExportModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-2xl p-8 max-w-lg w-full mx-4 space-y-6 border border-slate-200"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">Export Attendance</h2>
                  <p className="text-sm text-slate-500">Choose your export option</p>
                </div>
              </div>
              <button
                onClick={() => setIsExportModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors p-2 hover:bg-slate-100 rounded-lg"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Option 1: Current Month Export */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white rounded-xl border-2 border-slate-200 hover:border-blue-300 transition-all shadow-sm hover:shadow-md overflow-hidden"
            >
              <button
                onClick={handleCurrentMonthDownload}
                disabled={exportloading}
                className="w-full p-6 text-left disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 text-lg">Current Month</h3>
                      <p className="text-sm text-slate-500">{format(new Date(year, month - 1), "MMMM yyyy")}</p>
                    </div>
                  </div>
                  {exportloading ? (
                    <svg
                      className="animate-spin h-6 w-6 text-blue-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                      ></path>
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 text-slate-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </div>
              </button>
            </motion.div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 border-t border-slate-300"></div>
              <span className="text-slate-400 text-sm font-medium px-3 py-1 bg-slate-100 rounded-full">OR</span>
              <div className="flex-1 border-t border-slate-300"></div>
            </div>

            {/* Option 2: Date Range Export */}
            <motion.div
              whileHover={{ scale: 1.01 }}
              className="bg-white rounded-xl border-2 border-slate-200 hover:border-green-300 transition-all shadow-sm hover:shadow-md p-6 space-y-4"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 text-lg">Custom Date Range</h3>
                  <p className="text-sm text-slate-500">Select start and end dates</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleDateRangeDownload}
                disabled={exportloading || !startDate || !endDate}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold py-3.5 px-4 rounded-xl transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {exportloading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                      ></path>
                    </svg>
                    <span>Exporting...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span>Export Date Range</span>
                  </>
                )}
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      )}

      {/* Attendance Details Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-lg p-6 max-w-lg w-full space-y-4"
          >
            <div className="flex justify-between items-center border-b pb-2 mb-2">
              <h2 className="text-xl font-semibold">
                Attendance - {selectedEmployee}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {/* Legend */}
            <div className="grid grid-cols-3 gap-2 text-sm">
              {Object.entries(statusColors).map(([status, colorClass]) => (
                <div key={status} className="flex items-center gap-2">
                  <span className={`w-5 h-5 rounded ${colorClass}`}></span>
                  <span>{status}</span>
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-2 pt-2">
              {attendanceData.map((day) => (
                <div
                  key={day.id}
                  className={`p-3 text-center rounded ${statusColors[day.status] || "bg-gray-200 text-gray-800"}`}
                  title={day.status}
                >
                  {new Date(day.date).getDate()}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default MonthlyAttendanceReport;